import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { staffApi, formatApiError } from "@/lib/api";
// Iter-312 · Notification Isolation — Start-of-Day PNG capture + POST
// pipeline lives in NotificationEngine so the emailed image stays
// byte-identical to the operator's downloaded PNG. See SYSTEM_RULES.md.
import { dispatchStartOfDay } from "@/lib/NotificationEngine";
import { toast } from "sonner";

/**
 * Start-of-Day gate. The first time an employee opens the branch UI on a
 * new calendar day (in the branch's local timezone), we prompt them to
 * confirm "today is <local date>". Confirming stamps
 * `branch.active_business_date` — every order completed until the next
 * Start-of-Day belongs to that business day and shows the Void button.
 *
 * Runs quietly and non-blocking. If the branch has never been confirmed
 * or the last confirmed date is stale, the dialog appears; otherwise it
 * stays hidden.
 *
 * Bug fix (2026-09-22) — Stale local_today across midnight rollover.
 * If a manager left the branch tab open overnight, the original one-shot
 * `useEffect(fetch, [branchId])` never re-ran, so `state.local_today`
 * stayed on yesterday's date. The gate's open-check
 * `active_business_date !== local_today` then evaluated false (both
 * yesterday) and the dialog silently refused to reappear — operators
 * couldn't confirm the new day, so `fire_start_of_day_rules` never ran
 * and the Branch Overview email never dispatched.
 *
 * The gate now refreshes on window focus, tab visibility change, and a
 * 5-minute background interval so any day rollover is picked up
 * automatically. Confirm errors surface as a persistent toast (no
 * auto-dismiss) so a stale-date HTTP 400 can't be missed.
 */
export default function StartOfDayGate({ branchId }) {
  const [state, setState] = useState(null); // {timezone, local_today, active_business_date}
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const lastLocalTodayRef = useRef(null);

  const fetchBusinessDate = useCallback(async () => {
    if (!branchId) return;
    try {
      const r = await staffApi.get(`/branches/${branchId}/business-date`);
      const next = r.data || null;
      setState((prev) => {
        // If the branch's local date advanced since we last fetched,
        // pop the dialog again so the operator can confirm the new day.
        if (next && lastLocalTodayRef.current && lastLocalTodayRef.current !== next.local_today) {
          setDismissed(false);
        }
        lastLocalTodayRef.current = next ? next.local_today : lastLocalTodayRef.current;
        return next;
      });
    } catch {
      /* silent — gate simply won't render */
    }
  }, [branchId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await fetchBusinessDate();
    })();
    return () => { cancelled = true; };
  }, [fetchBusinessDate]);

  // Re-fetch on tab focus / visibility change / 5-min interval so a tab
  // left open overnight picks up the Bali-midnight rollover without a
  // manual page reload.
  useEffect(() => {
    if (!branchId) return undefined;
    const onFocus = () => { fetchBusinessDate(); };
    const onVisibility = () => { if (document.visibilityState === "visible") fetchBusinessDate(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    const iv = setInterval(fetchBusinessDate, 5 * 60 * 1000);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(iv);
    };
  }, [branchId, fetchBusinessDate]);

  const open = !!state && !dismissed && state.active_business_date !== state.local_today;

  const confirm = async () => {
    setBusy(true);
    // Iter-312 · Start-of-Day dispatch delegated to NotificationEngine.
    // The engine captures today's Branch Overview PNG in-browser,
    // POSTs it to `/branches/{bid}/business-date`, and returns the
    // fresh {active_business_date} envelope so the local state can
    // pick up the new business day without a refetch. If the PNG
    // capture fails the backend falls back to headless Chromium and
    // no email is lost.
    try {
      const data = await dispatchStartOfDay({ branchId, localToday: state.local_today });
      setState((s) => ({ ...s, active_business_date: data.active_business_date }));
      toast.success(`Business day started · ${state.local_today}`);
    } catch (e) {
      // Persistent toast (5s) — a stale-date HTTP 400 must never be missed.
      toast.error(formatApiError(e), { duration: 5000 });
      // Force a re-fetch so if the server has advanced, the gate
      // instantly re-presents with the correct local_today.
      fetchBusinessDate();
    }
    finally { setBusy(false); }
  };

  if (!state) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setDismissed(true); }}>
      <DialogContent className="max-w-md" data-testid="start-of-day-dialog">
        <DialogHeader>
          <DialogTitle>Start today&apos;s business day</DialogTitle>
          <DialogDescription>
            We detected today is <span className="font-semibold text-foreground" data-testid="start-of-day-date">{state.local_today}</span>{state.timezone ? ` (${state.timezone})` : ""}.
            Confirm to open the branch for business — every transaction taken until the next Start-of-Day counts as &quot;same day&quot; for Void vs Refund.
          </DialogDescription>
        </DialogHeader>
        <div className="text-xs text-muted-foreground">
          {state.active_business_date
            ? <>Last confirmed business day: <span className="font-medium text-foreground">{state.active_business_date}</span></>
            : <>No business day has been confirmed for this branch yet.</>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setDismissed(true)} data-testid="start-of-day-later-btn">Later</Button>
          <Button onClick={confirm} disabled={busy} data-testid="start-of-day-confirm-btn">
            {busy ? "Starting…" : "Confirm start of day"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
